import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import { useFreeformHandlers } from "@/lib/hooks/slides/editor/handlers/other/useFreeformHandlers";
import { useLocalAudioUpload } from "@/lib/hooks/slides/editor/useLocalAudioUpload";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setSelectedId } from "@/store/slideEditorSlice";
import { backgroundColors } from "@ai-teach/canvas-styles";
import type { LineElement } from "@ai-teach/types";
import { useRef } from "react";
import { DraggableElement } from "../../DraggableElement";
import { useSlideEditorContext } from "../../SlideEditorContext";
import { DraggableLine } from "../../renderers/DraggableLine";

export function FreeformCanvas() {
  const dispatch = useAppDispatch();
  const { slide, selectedId, canvasSize, scale } = useAppSelector(
    (state) => state.slideEditor.present
  );
  const { canvasRef } = useSlideEditorContext();
  // Deselect only when the whole press→release gesture was on bare canvas (not an element/its frame).
  const pressedBackground = useRef(false);
  const isBackground = (t: EventTarget | null) =>
    !(t as HTMLElement | null)?.closest?.("[data-element-id], .moveable-control-box");

  const { isGridVisible } = useSlideEditorContext();
  const freeformHandlers = useFreeformHandlers();
  const { handleFileDrop: handleImageFileDrop } = useLocalImageUpload((media) =>
    freeformHandlers.addImage?.(media)
  );
  const { handleFileDrop: handleAudioFileDrop } = useLocalAudioUpload((media) =>
    freeformHandlers.addAudio?.(media)
  );

  const handleLocalFileDrop = (file: File) => {
    if (file.type.startsWith("audio/")) {
      handleAudioFileDrop(file);
    } else {
      handleImageFileDrop(file);
    }
  };

  if (!slide) return null;

  const elements = slide.elements;

  return (
    <div
      ref={canvasRef}
      className="relative shadow-lg overflow-hidden"
      style={{
        height: canvasSize.height,
        aspectRatio: canvasSize.aspectRatio,
        transformOrigin: "center center",
        transform: `scale(${scale})`,
        backgroundColor: backgroundColors.canvasBg,
      }}
      onPointerDown={(e) => {
        pressedBackground.current = isBackground(e.target);
      }}
      onClick={(e) => {
        if (pressedBackground.current && isBackground(e.target)) {
          dispatch(setSelectedId(null));
        }
      }}
    >
      <DroppableZone
        id="FreeformCanvas"
        data={{ id: "FreeformCanvas", type: "FreeformCanvas" }}
        allowedType={["image", "video", "audio", "shape", "text", "line"]}
        className="h-full w-full"
        onLocalFileDrop={handleLocalFileDrop}
      >
        {isGridVisible && (
          <div
            data-screenshot-hide="false"
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `
      repeating-linear-gradient(
        to right,
        rgba(0,0,0,0.12) 0px,
        rgba(0,0,0,0.12) 1px,
        transparent 1px,
        transparent 30px
      ),
      repeating-linear-gradient(
        to bottom,
        rgba(0,0,0,0.12) 0px,
        rgba(0,0,0,0.12) 1px,
        transparent 1px,
        transparent 30px
      ),
      repeating-linear-gradient(
        to right,
        rgba(0,0,0,0.07) 0px,
        rgba(0,0,0,0.07) 1px,
        transparent 1px,
        transparent 10px
      ),
      repeating-linear-gradient(
        to bottom,
        rgba(0,0,0,0.07) 0px,
        rgba(0,0,0,0.07) 1px,
        transparent 1px,
        transparent 10px
      )
    `,
            }}
          />
        )}

        {elements
          .filter((el) => el.type !== "line")
          .map((element) => (
            <DraggableElement
              key={element.id}
              element={element}
              isSelected={element.id === selectedId}
              scale={scale}
            />
          ))}

        {elements
          .filter((el) => el.type === "line")
          .map((element) => (
            <DraggableLine
              key={element.id}
              element={element as LineElement}
              isSelected={element.id === selectedId}
              scale={scale}
            />
          ))}

        {elements.length === 0 && (
          <div className="absolute inset-0 flex justify-center items-center text-gray-500">
            Click on the sidebar to add elements
          </div>
        )}
      </DroppableZone>
    </div>
  );
}
